import { useRef, useCallback, useEffect } from "react";
import axios from "axios";

const originWhiteList = [
  "deepbrainai.io",
  "aistudios.com",
  "deepbrain.io",
];

async function directRequest(target: string) {
  const { data } = await axios.get(target, {
    responseType: "blob",
  });

  if (!data) {
    throw new Error("useRequestCors: fetch data error");
  }

  return data;
}

async function forwardRequest(target: string) {
  const { data } = await axios.get(`${origin}/api/utils/requestCors`, {
    params: { target }
  });

  if (!data || !data.success) {
    throw new Error("useRequestCors: fetch data error");
  }

  const { fileType, arrayBuffer } = data.data;
  const blob = new Blob([Uint8Array.from(arrayBuffer)], { type: fileType });

  return blob;
}

export function useRequestCors() {
  const objectUrls = useRef<string[]>([]);

  const requestCors = useCallback(
    async <T extends (Blob | string)>(
      url: string,
      type: "objectUrl" | "blob" = "objectUrl"
    ): Promise<T | null> => {
      try {
        if (!url) {
          return null;
        }

        const origin = new URL(location.href).origin;
        const target = url.startsWith("/") ? `${origin}${url}` : url;
        const targetOrigin = new URL(target).origin;

        const direct = (() => {
          if (origin === targetOrigin) {
            return true;
          }
          if (originWhiteList.some((white) => targetOrigin.endsWith(white))) {
            return true;
          }
          return false;
        })();

        const blob = direct
          ? await directRequest(target)
          : await forwardRequest(target);

        if (type === "blob") {
          return blob as T;
        }

        const objectUrl = URL.createObjectURL(blob);

        objectUrls.current = [...objectUrls.current, objectUrl];

        return objectUrl as T;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    []
);

  const revokeCors = useCallback(() => {
    objectUrls.current.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    objectUrls.current = [];
  }, []);

  useEffect(() => {
    return () => {
      revokeCors();
    };
  }, [revokeCors]);

  return { requestCors, revokeCors };
}
